import { lazy, Suspense, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Hero from "./Hero";
import HeroImage from "./HeroImage";
import KillerGF from "./KillerGF";
import Header from "../../components/Header";
import CurvedLoop from "../../components/CurvedLoop";
import { PagePlaceholder } from "../../components/PagePlaceholder";
import {
  DesktopOnly,
  HomeScrollRoot,
  MobileOnly,
  SnapSection,
} from "./homeLayout";

const HomeBelowFold = lazy(() => import("./HomeBelowFold"));

const HeroVisualSection = styled(SnapSection)`
  @media only screen and (max-width: 1000px) {
    min-height: auto;
    padding-bottom: 0.4rem;
    justify-content: flex-start;
  }
`;

const HeroContentSection = styled(SnapSection)`
  @media only screen and (max-width: 1000px) {
    min-height: auto;
    justify-content: flex-start;
  }
`;

const DesktopHeroContentSection = styled(HeroContentSection)`
  @media only screen and (max-width: 1000px) {
    display: none;
  }
`;

const MobileHeroBodySection = styled(HeroContentSection)`
  display: none;
  scroll-snap-align: none;
  scroll-snap-stop: normal;

  @media only screen and (max-width: 1000px) {
    display: flex;
  }
`;

const HeroScrollEndMarker = styled.div`
  width: 0;
  height: 0;
  overflow: hidden;
  pointer-events: none;
  flex: 0 0 0;
`;


const ScrollTopButton = styled.button<{ $visible: boolean }>`
  position: fixed;
  right: clamp(1.4rem, 2.2vw, 2.6rem);
  /* --scroll-top-bottom is set inline so per-pixel updates during scroll
     don't mint a new styled-components class each frame. */
  bottom: var(--scroll-top-bottom, clamp(1.4rem, 2.2vw, 2.6rem));
  width: clamp(3.6rem, 4.4vw, 4.4rem);
  height: clamp(3.6rem, 4.4vw, 4.4rem);
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.24);
  background: rgba(10, 10, 10, 0.86);
  color: var(--primary);
  font-family: "Space Grotesk", sans-serif;
  font-size: clamp(1.8rem, 2.1vw, 2.2rem);
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 60;
  cursor: pointer;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  visibility: ${({ $visible }) => ($visible ? "visible" : "hidden")};
  transform: ${({ $visible }) =>
    $visible ? "translateY(0) scale(1)" : "translateY(1rem) scale(0.92)"};
  transition: opacity 0.24s ease, transform 0.24s ease, visibility 0.24s ease,
    bottom 0.22s ease, background-color 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.28);

  &:hover {
    background: rgba(10, 10, 10, 0.96);
    box-shadow: 0 16px 32px rgba(0, 0, 0, 0.32);
  }
`;

const DEFAULT_SCROLL_TOP_BOTTOM_PX = 14;
const SCROLL_TOP_FOOTER_GAP_PX = 12;

const HomePage = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollTopButtonRef = useRef<HTMLButtonElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    document.documentElement.classList.add("home-snap-scroll");
    document.body.classList.add("home-snap-scroll");

    return () => {
      document.documentElement.classList.remove("home-snap-scroll");
      document.body.classList.remove("home-snap-scroll");
    };
  }, []);

  useEffect(() => {
    // Warm the below-fold chunk without competing with the hero images for
    // bandwidth during the LCP window.
    const idle = (window as any).requestIdleCallback as
      | ((cb: () => void, opts?: { timeout: number }) => number)
      | undefined;
    if (idle) {
      idle(() => void import("./HomeBelowFold"), { timeout: 4000 });
      return;
    }
    const timeoutId = window.setTimeout(
      () => void import("./HomeBelowFold"),
      2500
    );
    return () => window.clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, "");
    if (!hash) return;

    let cancelled = false;

    const scrollToTarget = (el: HTMLElement) => {
      const container = scrollContainerRef.current;
      const behavior = window.innerWidth <= 1000 ? "auto" : "smooth";
      if (container && container.scrollHeight > container.clientHeight) {
        const top =
          el.getBoundingClientRect().top -
          container.getBoundingClientRect().top +
          container.scrollTop;
        container.scrollTo({ top, behavior });
        return;
      }
      el.scrollIntoView({ behavior, block: "start" });
    };

    // Below-fold sections mount lazily, so the target may not exist yet:
    // resolve the chunk first, then retry across a few frames while the
    // section renders.
    const tryScroll = (attempt: number) => {
      if (cancelled) return;
      const el = document.getElementById(hash);
      if (el) {
        requestAnimationFrame(() => {
          if (cancelled) return;
          scrollToTarget(el);
          // Content above the target can still be expanding (lazy sections,
          // late images); correct the position once things settle.
          window.setTimeout(() => !cancelled && scrollToTarget(el), 800);
        });
        return;
      }
      if (attempt < 40) {
        window.setTimeout(() => tryScroll(attempt + 1), 100);
      }
    };

    import("./HomeBelowFold").finally(() => tryScroll(0));

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let frame = 0;

    const setButtonBottom = (bottomPx: number | null) => {
      const button = scrollTopButtonRef.current;
      if (!button) return;
      if (bottomPx == null) {
        button.style.removeProperty("--scroll-top-bottom");
      } else {
        button.style.setProperty("--scroll-top-bottom", `${bottomPx}px`);
      }
    };

    const updateScrollTopUi = () => {
      const heroEnd = document.getElementById("hero-scroll-end");
      if (heroEnd) {
        const heroEndTop = heroEnd.getBoundingClientRect().top;
        setShowScrollTop(heroEndTop < window.innerHeight);
      }

      if (window.innerWidth > 1000) {
        setButtonBottom(null);
        return;
      }

      const footer = document.getElementById("home-footer");
      if (!footer) {
        setButtonBottom(null);
        return;
      }

      const footerTop = footer.getBoundingClientRect().top;
      const viewportHeight = window.innerHeight;

      if (footerTop < viewportHeight) {
        const liftedBottom =
          viewportHeight - footerTop + SCROLL_TOP_FOOTER_GAP_PX;
        setButtonBottom(Math.max(DEFAULT_SCROLL_TOP_BOTTOM_PX, liftedBottom));
      } else {
        setButtonBottom(null);
      }
    };

    // Batch layout reads to one rAF per frame instead of per scroll event.
    const scheduleUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        updateScrollTopUi();
      });
    };

    const container = scrollContainerRef.current;
    updateScrollTopUi();
    container?.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);

    return () => {
      container?.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  const scrollToTop = () => {
    const container = scrollContainerRef.current;
    container?.scrollTo({ top: 0, behavior: "smooth" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    // tabIndex lets keyboard users focus the custom scroll container and
    // scroll it with arrow/page keys (document scrolling is disabled on
    // desktop for snap behavior).
    <HomeScrollRoot id="home-scroll-root" ref={scrollContainerRef} tabIndex={0}>
      <HeroVisualSection
        style={{
          justifyContent: "flex-start",
        }}
      >
        <Header />
        <MobileOnly>
          <Hero
            scrollContainerRef={scrollContainerRef}
            showBody={false}
            mobileVariant="titleOnly"
          />
        </MobileOnly>
        <DesktopOnly>
          <CurvedLoop
            marqueeText="welcome to killer gf ✦"
            speed={1}
            curveAmount={-100}
            direction="right"
            interactive={false}
          />
        </DesktopOnly>
        <HeroImage scrollContainerRef={scrollContainerRef} />
      </HeroVisualSection>

      <DesktopHeroContentSection>
        <Hero scrollContainerRef={scrollContainerRef} />
      </DesktopHeroContentSection>

      <MobileHeroBodySection>
        <Hero
          scrollContainerRef={scrollContainerRef}
          showTitle={false}
          mobileVariant="bodyOnly"
        />
      </MobileHeroBodySection>

      <HeroScrollEndMarker id="hero-scroll-end" aria-hidden="true" />

      <SnapSection $background="#ffffff">
        <KillerGF />
      </SnapSection>

      <Suspense
        fallback={<PagePlaceholder $minHeight="100svh" $shimmer aria-hidden />}
      >
        <HomeBelowFold />
      </Suspense>

      <ScrollTopButton
        ref={scrollTopButtonRef}
        type="button"
        aria-label="Scroll to top"
        onClick={scrollToTop}
        $visible={showScrollTop}
      >
        ↑
      </ScrollTopButton>
    </HomeScrollRoot>
  );
};

export default HomePage;
